import {Component, input, OnDestroy} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {Observable, of, Subscription, tap} from 'rxjs';
import {
  InputConnectionConfigurationComponent
} from '../input-connection-configuration/input-connection-configuration.component';
import {
    Input,
    InputConnection,
    SensorConnectionType,
    VehicleHardware,
    VehicleHardwareType
} from '../model/plowops-pro-models';
import {MatFormField, MatInput} from '@angular/material/input';
import {delay, filter, map} from 'rxjs/operators';
import {MatOption, MatSelect} from '@angular/material/select';

@Component({
  selector: 'app-plow-configuration',
    imports: [
        ReactiveFormsModule,
        InputConnectionConfigurationComponent,
        MatFormField,
        MatInput,
        MatFormField,
        MatOption,
        MatSelect,
    ],
  templateUrl: './plow-configuration.component.html',
  styleUrls: ['../../../../settings-fields.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PlowConfigurationComponent
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      multi: true,
      useExisting: PlowConfigurationComponent
    },
  ]
})
export class PlowConfigurationComponent implements ControlValueAccessor, Validator, OnDestroy {

  constructor(fb: FormBuilder) {
    this.formGroup = fb.group({
      label: fb.control(null),
      connection: fb.control(null),
      twoWireOtherConnection: fb.control(null),
      sensorConnectionType: fb.control(SensorConnectionType.SINGLE_WIRE)
    });

    this.subscriptions.push(this.formGroup.events.pipe(
        filter(e => e instanceof TouchedChangeEvent && e.touched)
      ).subscribe(() => this.onTouchedListener())
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.subscribe(
        value => {
          const {
            connection,
            label,
            twoWireOtherConnection,
            sensorConnectionType,
          } = value || null;

          this.onChangeListener({
            type: VehicleHardwareType.PLOW,
            connection,
            plowConfiguration: {
              label,
              twoWireOtherConnection: sensorConnectionType === SensorConnectionType.TWO_WIRES ? twoWireOtherConnection : null
            }
          } as VehicleHardware);
        }
      )
    );

    this.subscriptions.push(
      this.formGroup.controls.sensorConnectionType.valueChanges.subscribe(value => {
        const inputConnection = this.formGroup.controls.connection.value as InputConnection;
        inputConnection.bluetooth = value === SensorConnectionType.BLUETOOTH;
        this.formGroup.controls.connection.setValue(inputConnection);

        if (value === SensorConnectionType.TWO_WIRES) {
          this.formGroup.controls.twoWireOtherConnection.enable({emitEvent: false});
        } else {
          this.formGroup.controls.twoWireOtherConnection.setValue(null, {emitEvent: false});
          this.formGroup.controls.twoWireOtherConnection.disable({emitEvent: false});
        }
      })
    );
  }

  formGroup: FormGroup;

  availableInputs = input<Input[]>();

  subscriptions: Subscription[] = [];

  protected readonly input = input;

  sensorConnectionTypes = Object.values(SensorConnectionType);

  isTwoWire(): boolean {
    return this.formGroup.controls.sensorConnectionType.value === SensorConnectionType.TWO_WIRES;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  writeValue(obj: VehicleHardware) {
    const {connection, plowConfiguration} = obj || {};

    let sensorConnectionType: SensorConnectionType;
    if (connection?.bluetooth) {
        sensorConnectionType = SensorConnectionType.BLUETOOTH;
    } else if (plowConfiguration?.twoWireOtherConnection != null) {
        sensorConnectionType = SensorConnectionType.TWO_WIRES;
    } else {
        sensorConnectionType = SensorConnectionType.SINGLE_WIRE;
    }

    this.formGroup.setValue({
      connection: connection || null,
      label: plowConfiguration?.label || null,
      twoWireOtherConnection: plowConfiguration?.twoWireOtherConnection || null,
      sensorConnectionType: sensorConnectionType || null
    });
  }

  onChangeListener = _ => {
  }

  onTouchedListener = () => {
  }

  registerOnChange(fn: any) {
    this.onChangeListener = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedListener = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formGroup.disable({emitEvent: false});
    } else {
      this.formGroup.enable({emitEvent: false});
    }
  }

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    // workaround for https://github.com/angular/angular/issues/42815
    return of(null)
      .pipe(
        delay(10),
        map(() => this.formGroup.invalid ? {invalid: true} : null),
        tap(() => control.parent?.updateValueAndValidity())
      );
  }
}
